import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCloudResourceStore } from '@/stores/cloudResourceStore';
import { toast } from 'sonner';
import {
  CLOUD_RESOURCE_CATEGORIES,
  CLOUD_RESOURCE_ENVIRONMENTS,
  CLOUD_RESOURCE_CRITICALITIES,
  type CloudResourceInsert
} from '@/types/cloudResource';
import { UserSelector } from '@/components/shared/UserSelector';

interface CloudResourceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cloudServiceId: string;
  onSuccess?: () => void;
}

export function CloudResourceFormDialog({
  open,
  onOpenChange,
  cloudServiceId,
  onSuccess
}: CloudResourceFormDialogProps) {
  const { addCloudResource } = useCloudResourceStore();
  const [loading, setLoading] = useState(false);
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [formData, setFormData] = useState({
    name: '',
    resource_type: '',
    resource_id: '',
    sku: '',
    region: '',
    environment: '',
    criticality: '',
    notes: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.resource_type) {
      toast.error('Name and Resource Type are required');
      return;
    }

    setLoading(true);
    try {
      const insertData: CloudResourceInsert = {
        org_id: '', // Will be set by store
        cloud_service_id: cloudServiceId,
        name: formData.name,
        resource_type: formData.resource_type,
        resource_id: formData.resource_id || undefined,
        sku: formData.sku || undefined,
        region: formData.region || undefined,
        environment: formData.environment as any || undefined,
        criticality: formData.criticality as any || undefined,
        notes: formData.notes || undefined,
        owner_id: ownerId,
      };

      const result = await addCloudResource(insertData);
      if (result) {
        toast.success('Resource added successfully');
        setFormData({
          name: '',
          resource_type: '',
          resource_id: '',
          sku: '',
          region: '',
          environment: '',
          criticality: '',
          notes: ''
        });
        setOwnerId(undefined);
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast.error('Failed to add resource');
      }
    } catch (error) {
      toast.error('Error adding resource');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Cloud Resource</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., prod-web-vm-01"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="resource_type">Resource Type *</Label>
            <Select
              value={formData.resource_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, resource_type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CLOUD_RESOURCE_CATEGORIES).map(([category, types]) => (
                  <div key={category}>
                    <div className="px-2 py-1 text-xs font-semibold text-muted-foreground">{category}</div>
                    {types.map(type => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resource_id">Resource ID / ARN</Label>
            <Input
              id="resource_id"
              value={formData.resource_id}
              onChange={(e) => setFormData(prev => ({ ...prev, resource_id: e.target.value }))}
              placeholder="e.g., arn:aws:ec2:..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sku">SKU / Tier</Label>
              <Input
                id="sku"
                value={formData.sku}
                onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                placeholder="e.g., Standard_D2s_v3"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="region">Region</Label>
              <Input
                id="region"
                value={formData.region}
                onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                placeholder="e.g., eu-west-1"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="environment">Environment</Label>
              <Select
                value={formData.environment}
                onValueChange={(value) => setFormData(prev => ({ ...prev, environment: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {CLOUD_RESOURCE_ENVIRONMENTS.map(env => (
                    <SelectItem key={env} value={env}>{env}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="criticality">Criticality</Label>
              <Select
                value={formData.criticality}
                onValueChange={(value) => setFormData(prev => ({ ...prev, criticality: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {CLOUD_RESOURCE_CRITICALITIES.map(crit => (
                    <SelectItem key={crit} value={crit}>{crit}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
            />
          </div>

          <UserSelector
            value={ownerId}
            onChange={setOwnerId}
            label="Owner"
            placeholder="Select owner"
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Resource'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
